import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import { CheckCircle2, XCircle, UserMinus, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

const FILTERS = ['pending', 'approved', 'rejected', 'inactive', 'all'];

function StatusBadge({ status }) {
  const styles = {
    approved: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-800',
    rejected: 'bg-red-100 text-red-700',
    inactive: 'bg-gray-100 text-gray-700',
  };
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${styles[status] || 'bg-gray-100'}`}>{status}</span>;
}

function RoleBadge({ role }) {
  const styles = {
    pastor: 'bg-purple-200 text-purple-900',
    admin: 'bg-amber-200 text-amber-900',
    member: 'bg-blue-100 text-blue-800',
  };
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${styles[role] || styles.member}`}>{role}</span>;
}

function MemberActions({ member, isPastor, onStatusChange, onRoleChange }) {
  return (
    <div className="flex flex-wrap gap-1">
      {member.status === 'pending' && (
        <>
          <button onClick={() => onStatusChange(member.id, 'approved')} className="text-xs px-2 py-1 rounded bg-green-600 text-white" data-testid={`approve-${member.id}`}>
            <CheckCircle2 size={12} className="inline"/> Approve
          </button>
          <button onClick={() => onStatusChange(member.id, 'rejected')} className="text-xs px-2 py-1 rounded bg-red-600 text-white">
            <XCircle size={12} className="inline"/> Reject
          </button>
        </>
      )}
      {member.status === 'approved' && (
        <button onClick={() => onStatusChange(member.id, 'inactive')} className="text-xs px-2 py-1 rounded bg-gray-500 text-white">
          <UserMinus size={12} className="inline"/> Inactivate
        </button>
      )}
      {member.status === 'inactive' && (
        <button onClick={() => onStatusChange(member.id, 'approved')} className="text-xs px-2 py-1 rounded bg-green-600 text-white">
          <UserCheck size={12} className="inline"/> Reactivate
        </button>
      )}
      {isPastor && member.status === 'approved' && (
        <select className="text-xs px-2 py-1 rounded border" value={member.role}
          onChange={(e) => onRoleChange(member.id, e.target.value)}
          data-testid={`role-select-${member.id}`}>
          <option value="member">member</option>
          <option value="admin">admin</option>
          <option value="pastor">pastor</option>
        </select>
      )}
    </div>
  );
}

function useMembersData(filter) {
  return useQuery({
    queryKey: ['adm-members', filter],
    queryFn: async () => {
      const params = filter === 'all' ? {} : { status: filter };
      return (await api.get('/admin/members', { params })).data;
    },
  });
}

export default function MembersTab({ isPastor }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('pending');
  const { data: members = [] } = useMembersData(filter);

  const statusMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/admin/members/${id}/status`, { status })).data,
    onSuccess: () => { toast.success('Member updated'); qc.invalidateQueries({ queryKey: ['adm-members'] }); },
  });

  const roleMut = useMutation({
    mutationFn: async ({ id, role }) => (await api.put('/pastor/assign-role', { profile_id: id, role })).data,
    onSuccess: () => { toast.success('Role updated'); qc.invalidateQueries({ queryKey: ['adm-members'] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not update'),
  });

  const onStatusChange = (id, status) => statusMut.mutate({ id, status });
  const onRoleChange = (id, role) => roleMut.mutate({ id, role });

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase ${filter === s ? 'bg-moj-purple text-white' : 'bg-white border border-moj-gold/30 text-moj-purple-deep'}`}
            data-testid={`members-filter-${s}`}>{s}</button>
        ))}
      </div>
      <div className="card-moj overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-moj-cream text-moj-purple-deep">
            <tr><th className="text-left p-3">Member</th><th className="text-left p-3">Email</th><th className="text-left p-3">Status</th><th className="text-left p-3">Role</th><th className="text-left p-3">Actions</th></tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-t border-moj-gold/20" data-testid={`member-row-${m.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{m.full_name || '—'}</div>
                  {m.phone && <div className="text-xs text-moj-charcoal/60">{m.phone}</div>}
                </td>
                <td className="p-3 text-xs">{m.google_email}</td>
                <td className="p-3"><StatusBadge status={m.status}/></td>
                <td className="p-3"><RoleBadge role={m.role}/></td>
                <td className="p-3">
                  <MemberActions member={m} isPastor={isPastor}
                    onStatusChange={onStatusChange} onRoleChange={onRoleChange}/>
                </td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr><td colSpan={5} className="text-center p-6 text-moj-charcoal/60">No members in this category.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
